// components/player.js
import Plyr from 'plyr/dist/plyr.js';
import 'plyr/dist/plyr.css';

export default function initPlayer() {
  const wrapper = document.querySelector('.player');
  const playerEl = wrapper?.querySelector('video#player');
  const closeBtn = document.querySelector('.player__close');

  if (!wrapper || !playerEl) return;

  let triggerButton = null;
  let youtubePlayer = null;

  // Helper Functions
  const show = () => {
    wrapper.classList.add('show');
    wrapper.setAttribute('aria-hidden', 'false');
  };

  const hide = () => {
    wrapper.classList.remove('show');
    wrapper.setAttribute('aria-hidden', 'true');

    // Clean up YouTube iframe if present
    const ytIframe = wrapper.querySelector('iframe[src*="youtube"]');
    if (ytIframe) {
      ytIframe.remove();
    }

    // Stop Plyr if playing
    if (window.playerInstance) {
      try { window.playerInstance.stop(); } catch (e) { }
    }

    // Show video element again
    if (playerEl) {
      playerEl.style.display = '';
    }

    if (triggerButton && typeof triggerButton.focus === 'function') {
      try { triggerButton.focus(); } catch (e) { }
    }
    triggerButton = null;
  };

  const getYoutubeId = (url = '') => {
    const patterns = [
      /(?:youtube\.com\/.*[?&]v=)([^&]+)/i,
      /youtu\.be\/([^?&#]+)/i,
      /youtube\.com\/shorts\/([^?&#]+)/i
    ];
    for (const rx of patterns) {
      const m = url.match(rx);
      if (m && m[1]) return m[1];
    }
    if (url.includes('v=')) return url.split('v=')[1].split('&')[0];
    return url;
  };

  const guessMime = (src, fallback = 'video/mp4') => {
    const ext = (src.split('?')[0].split('#')[0].split('.').pop() || '').toLowerCase();
    if (ext === 'webm') return 'video/webm';
    if (ext === 'ogv' || ext === 'ogg') return 'video/ogg';
    if (ext === 'm3u8') return 'application/x-mpegURL';
    return fallback;
  };

  const loadYouTubeVideo = (videoId) => {
    // Hide the video element
    playerEl.style.display = 'none';

    // Remove any existing YouTube iframe
    const existingIframe = wrapper.querySelector('iframe[src*="youtube"]');
    if (existingIframe) {
      existingIframe.remove();
    }

    // Create new YouTube iframe
    const iframe = document.createElement('iframe');
    iframe.setAttribute('allowfullscreen', '');
    iframe.setAttribute('class', 'plyr');
    iframe.setAttribute('allow', 'accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture');
    iframe.src = `https://www.youtube.com/embed/${videoId}?enablejsapi=1&origin=${window.location.origin}&rel=0&modestbranding=1`;

    wrapper.querySelector('.container').appendChild(iframe);

    console.log('YouTube iframe loaded with sound enabled');
  };

  const loadVideoFile = (src, type, title, poster, captionsUrl) => {
    // Show video element
    playerEl.style.display = '';

    // Remove any YouTube iframe
    const ytIframe = wrapper.querySelector('iframe[src*="youtube"]');
    if (ytIframe) ytIframe.remove();

    // Destroy old Plyr instance
    if (window.playerInstance) {
      try { window.playerInstance.destroy(); } catch (e) { }
      window.playerInstance = null;
    }

    // Create fresh Plyr for video file, ensure captions are enabled
    window.playerInstance = new Plyr(playerEl, {
      fullscreen: { enabled: true, fallback: true, iosNative: true, container: null },
      volume: 1,
      muted: false,
      captions: { active: true, update: true, language: 'en' }
    });

    const sourceObj = {
      type: 'video',
      title: title || '',
      poster: poster || undefined,
      sources: [{ src, type }]
    };

    if (captionsUrl) {
      sourceObj.tracks = [{
        kind: 'captions',
        label: 'English',
        srclang: 'en',
        src: captionsUrl,
        default: true
      }];
    }

    // Set source
    window.playerInstance.source = sourceObj;

    window.playerInstance.once('ready', () => {
      console.log('Video file ready');
    });
  };

  // Close button
  if (closeBtn) {
    closeBtn.addEventListener('click', () => {
      hide();
    });
  }

  // Close on Escape key
  const handleKeyDown = (e) => {
    if (e.key === 'Escape' && wrapper.classList.contains('show')) {
      hide();
    }
  };

  document.addEventListener('keydown', handleKeyDown);

  // Wire YouTube Buttons
  const ytBtns = Array.from(document.querySelectorAll('[data-youtube]'));
  ytBtns.forEach((btn) => {
    btn.addEventListener('click', (e) => {
      if (btn.tagName === 'A') e.preventDefault();
      const id = getYoutubeId(btn.dataset.youtube || '');
      if (!id) return;

      triggerButton = btn;
      show();
      loadYouTubeVideo(id);
    });
  });

  // Wire Video File Buttons
  const fileBtns = Array.from(document.querySelectorAll('[data-video]'));
  fileBtns.forEach((btn) => {
    btn.addEventListener('click', (e) => {
      if (btn.tagName === 'A') e.preventDefault();
      const src = btn.dataset.video;
      if (!src) return;

      const type = btn.dataset.type || guessMime(src);
      const captionsUrl = btn.dataset.captions || btn.dataset.transcript;

      triggerButton = btn;
      show();
      loadVideoFile(src, type, btn.dataset.title, btn.dataset.poster, captionsUrl);
    });
  });
}